import React, { useState, useEffect } from "react";
import { ListChecks, Calendar, X } from "lucide-react";

/**
 * Asks the user whether a just-completed item should also be added to the
 * "Remember" panel with a due date.
 *
 *  - "Yes"  -> onConfirm(dueDate): mark the item complete AND add to Remember
 *              (a due date is required).
 *  - "No"   -> onDecline(): mark the item complete WITHOUT adding to Remember.
 *  - Close (X / backdrop) -> onClose(): cancel entirely, the item stays UNMARKED.
 */
const RememberPromptModal = ({ isOpen, itemName, onConfirm, onDecline, onClose }) => {
  const [dueDate, setDueDate] = useState("");

  useEffect(() => {
    if (isOpen) setDueDate("");
  }, [isOpen, itemName]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!dueDate) return;
    onConfirm(dueDate);
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-3xl p-6 sm:p-8 max-w-md w-full border-2 border-cyan-500/30 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ListChecks className="w-6 h-6 text-cyan-400" />
            <h3 className="text-xl font-bold text-cyan-400">Add to Remember?</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors"
            title="Cancel (leave the task unmarked)"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <p className="text-cyan-200 mb-4">
          Do you want to add{" "}
          <span className="font-semibold text-yellow-400 break-words">
            "{itemName}"
          </span>{" "}
          to your Remember list with a due date?
        </p>

        <div className="mb-6">
          <label className="flex items-center gap-2 text-sm text-cyan-300 mb-2">
            <Calendar className="w-4 h-4 text-cyan-400" />
            Due Date (required)
          </label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            autoFocus
            className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-xl border-2 border-gray-600 focus:border-cyan-500 outline-none"
          />
          {!dueDate && (
            <p className="text-xs text-gray-400 mt-2">
              Pick a due date to add it to Remember.
            </p>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleConfirm}
            disabled={!dueDate}
            className="flex-1 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-xl transition-all"
          >
            Yes
          </button>
          <button
            onClick={onDecline}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-xl transition-all"
          >
            No
          </button>
        </div>
      </div>
    </div>
  );
};

export default RememberPromptModal;
